import React, { useEffect, useState } from "react";
import AdminDashboard from "../Layout/AdminDashboard";
import {
  Table,
  Button,
  Space,
  Modal,
  message,
  Dropdown,
  Menu,
  Typography,
} from "antd";
import axios from "axios";
import moment from "moment";
import {
  CheckOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  MailOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import SendMailModal from "./SendMail";
import { useNavigate } from "react-router-dom";

const { confirm } = Modal;

function WebsiteEnquires() {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [selectionType] = useState("checkbox");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [sentMailId, setSentMailId] = useState("");
  const [formLink, setFormLink] = useState("");
  const toggleModal = () => {
    setIsModalVisible(!isModalVisible);
    setIsModalOpen(!isModalOpen);
  };
  const closeSendMailModal = () => setIsModalVisible(false);

  const handleSuccess = (mailId, formLink) => {
    setSentMailId(mailId);
    setFormLink(formLink);
    setIsSuccessModalVisible(true);
  };

  const handleSuccessModalClose = () => {
    setIsSuccessModalVisible(false);
    closeSendMailModal();
  };

  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 8,
      total: 0,
    },
  });

  const apiUrl = process.env.REACT_APP_API_URL;

  const handleMenuClick = (record, { key }) => {
    switch (key) {
      case "view":
        navigate(`/client-profile/add-business`);
        break;
      case "delete":
        // showSingleDeleteConfirm(record._id); // Pass the correct record ID
        break;
      case "formLink":
        toggleModal();
        break;
      default:
        break;
    }
  };

  const menu = (record) => (
    <Menu
      onClick={(e) => handleMenuClick(record, e)}
      style={{ padding: "8px" }}
    >
      <Menu.Item
        key="view"
        style={{ margin: "8px 0", backgroundColor: "#FFE0B2" }}
      >
        <span
          style={{ color: "#E65100", fontWeight: "bold", fontSize: "12px" }}
        >
          <EyeOutlined /> Fill Form
        </span>
      </Menu.Item>
      <Menu.Item
        key="formLink"
        style={{ margin: "8px 0", backgroundColor: "#BBDEFB" }} // light blue
      >
        <span
          style={{ color: "#0D47A1", fontWeight: "bold", fontSize: "12px" }} // dark blue
        >
          <MailOutlined /> Send Link
        </span>
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: "Business Name",
      dataIndex: "businessName",
      key: "businessName",
    },
    {
      title: "Full Name",
      dataIndex: "fullName",
      key: "fullName",
    },
    {
      title: "Phone Number",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
    },
    {
      title: "Mail ID",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Service",
      dataIndex: "service",
      key: "service",
    },
    {
      title: "Created On",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => moment(text).format("DD MMM YYYY, hh:mm A"),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Dropdown
          overlay={menu(record)}
          trigger={["click"]}
          placement="bottomLeft"
          arrow
          danger
        >
          <Button type="link" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const rowSelection = {
    onChange: (keys, rows) => {
      setSelectedRowKeys(keys);
      setSelectedRows(rows);
    },
  };

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${apiUrl}/api/marketingwebsite/contact-users`
      );
      const data = res.data?.data || [];

      const paginatedData = data.map((item, index) => ({
        ...item,
        key: item._id || index,
      }));

      setEnquiries(paginatedData);
      setTableParams((prev) => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          total: data.length,
        },
      }));
    } catch (error) {
      console.error("Error fetching website enquiries:", error);
      message.error("Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const showDeleteConfirm = () => {
    if (selectedRows.length === 0) {
      return message.warning("No enquiries selected for deletion");
    }

    confirm({
      title: "Are you sure you want to delete selected enquiries?",
      icon: <ExclamationCircleOutlined />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        const idsToDelete = selectedRows.map((row) => row._id);
        axios
          .patch(`${apiUrl}/api/marketingwebsite/remove-enquiry`, {
            data: { ids: idsToDelete },
          })
          .then(() => {
            message.success("Selected enquiries deleted");
            fetchEnquiries();
            setSelectedRowKeys([]);
            setSelectedRows([]);
          })
          .catch((err) => {
            console.error(err);
            message.error("Error deleting selected enquiries");
          });
      },
    });
  };

  const handleTableChange = (pagination) => {
    setTableParams({
      pagination,
    });
  };

  return (
    <AdminDashboard>
      <div className="m-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Website Enquiries</h2>
          <Space>
            <Button
              type="primary"
              danger
              onClick={showDeleteConfirm}
              disabled={selectedRowKeys.length === 0}
            >
              Delete Selected
            </Button>
          </Space>
        </div>

        <Table
          rowSelection={{
            type: selectionType,
            ...rowSelection,
          }}
          columns={columns}
          dataSource={enquiries}
          rowKey="key"
          pagination={tableParams.pagination}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
      <SendMailModal
        visible={isModalVisible}
        onCancel={closeSendMailModal}
        onSuccess={handleSuccess}
      />
      <Modal
        visible={isSuccessModalVisible}
        onCancel={handleSuccessModalClose}
        footer={null}
      >
        <div className="flex items-center justify-center">
          <CheckOutlined className="text-green-500 text-4xl" />
        </div>
        <div className="text-center mb-4">
          <Typography.Text strong>Mail Sent To:</Typography.Text>
          <Typography.Text className="block my-2">{sentMailId}</Typography.Text>
        </div>
        <div className="text-center mb-4">
          <Typography.Text strong>
            Copy form link:{" "}
            <Typography.Link
              href={formLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline"
            >
              {formLink}
            </Typography.Link>
          </Typography.Text>
        </div>
      </Modal>
    </AdminDashboard>
  );
}

export default WebsiteEnquires;
